import { denormalize } from 'normalizr';
import mapValues from 'lodash/mapValues';

import { schemaMap } from '@store/schemas';
import {
  compareByAvailability,
  sortEntityByDisplayOrder,
  sortEntityByPriority,
} from '@utils/helpers';

import { createSelector } from 'reselect';

const getEntireEntityList = (entityName) => (state) => state.entities[entityName].byId;

const getConfigurationValidatorList = () => (state) => state.entities.validator;

const entityListSelector = (entityName) =>
  createSelector(getEntireEntityList(entityName), (items) =>
    Object.entries(items).map(([_key, value]) => value)
  );

const entityListSelectorByCategory = (entityName) =>
  createSelector(
    getEntireEntityList(entityName),
    (state, id) => id,
    (items, categoryId) => {
      const id = parseInt(categoryId, 10);
      return Object.entries(items)
        .map(([_key, value]) => value)
        .filter((entry) => {
          const categories = entry.categories || [];
          const entryCategories = categories.map((el) => el.id);
          return entryCategories.includes(id);
        });
    }
  );

export const getEntityItemSelector = (entityName) => (state, id) =>
  state.entities[entityName].byId[id || state.entities[entityName].allIds[0]];

const getDenormalizedEntitySelector = (entityName) => (state, id) => {
  const entities = mapValues(state.entities, (entities) => entities.byId);

  return denormalize(state.entities[entityName].byId[id], schemaMap[entityName], entities);
};

const getPageBySlugName = (entityName) => (state, slug) => {
  const entities = mapValues(state.entities, (entities) => entities.byId);

  return denormalize(
    Object.values(state.entities[entityName].byId).find((entity) => entity.slug === slug),
    schemaMap[entityName],
    entities
  );
};

const getPageFromProperty = (slugName) => (state) =>
  Object.values(state.entities.pages.byId).find(({ slug }) => slug === slugName);

const getSpaEntityItemsList = (entityName, categoryId, availabilityDate = new Date()) =>
  createSelector(entityListSelector(entityName), (items) => {
    if (!categoryId) {
      return items.sort((a, b) => compareByAvailability(a, b, availabilityDate));
    }

    return items
      .filter(({ categories }) => categories.find(({ id }) => id === categoryId))
      .sort((a, b) => compareByAvailability(a, b, availabilityDate));
  });

const getSpaPackagesEntityItemsList = (entityName, categoryId) =>
  createSelector(entityListSelector(entityName), (items) => {
    if (!categoryId) {
      return items;
    }

    return items.filter(({ categories }) => categories.find(({ id }) => id === categoryId));
  });

const getSpaItemsListSortedByPriority = (entityName) =>
  createSelector(getSpaEntityItemsList(entityName), (spaItems) => sortEntityByPriority(spaItems));

const getItemsListSortedByDisplayOrder = (entityName) =>
  createSelector(entityListSelector(entityName), (items) => sortEntityByDisplayOrder(items));

const getPaginatedEntityList = (entityName) => (state) => state.pagination[entityName];

export const entitySelectors = {
  isRoomsEnabled: getEntityItemSelector('properties').modulesExcludingDisabled?.find(
    (module) => module.rooms
  ),
  getProperty: getEntityItemSelector('properties') || {},
  getPropertyName: getEntityItemSelector('properties').name,
  getProduct: getEntityItemSelector('products'),
  getCategory: getEntityItemSelector('categories'),
  getHomepage: getDenormalizedEntitySelector('homepages'),
  getAllProducts: entityListSelector('products'),
  getAllPackagings: entityListSelector('packagings'),
  getAllCategories: entityListSelector('categories'),
  getSpaClassesCategories: (state) => {
    const categories = entityListSelector('categories')(state);
    return categories.filter(({ hasSpaClasses }) => hasSpaClasses);
  },
  isSpaDaysEnabled: (state) => {
    const property = getEntityItemSelector('properties')(state);
    return property?.modulesExcludingDisabled?.spa?.features?.days?.enabled;
  },
  isTreatmentEnabled: (state) => {
    const property = getEntityItemSelector('properties')(state);
    return property?.modulesExcludingDisabled?.spa?.features?.treatments?.enabled;
  },
  isFacilityEnabled: (state) => {
    const property = getEntityItemSelector('properties')(state);
    return property?.modulesExcludingDisabled?.spa?.features?.facilities?.enabled;
  },
  isClassEnabled: (state) => {
    const property = getEntityItemSelector('properties')(state);
    return property?.modulesExcludingDisabled?.spa?.features?.classes?.enabled;
  },
  isSpaShowGenderNeutralEnabled: (state) => {
    const property = getEntityItemSelector('properties')(state);
    return property?.modulesExcludingDisabled?.spa?.features?.showGenderNeutral?.enabled;
  },
  getCategoryProducts: entityListSelectorByCategory('products'),
  getCategoryFacilities: entityListSelectorByCategory('facilities'),
  getCategorySpaDays: entityListSelectorByCategory('spaDays'),
  getPage: getPageBySlugName('pages'),
  getTermsAndConditionPage: getPageFromProperty('terms-and-conditions'),
  getPrivacyAndCookiePolicyPage: getPageFromProperty('privacy-and-cookies'),
  getCancellationPage: getPageFromProperty('cancellation'),
  getFacilitiesPage: getPageFromProperty('facilities'),
  getSpaDaysPage: getPageFromProperty('spa-days'),
  getSpaPage: getPageFromProperty('spa'),
  getTablesPage: getPageFromProperty('tables'),
  getRoomsPage: getPageFromProperty('rooms'),
  getGolfPage: getPageFromProperty('golf'),
  getGolfPayAndPlayPage: getPageFromProperty('golf-pay-and-play'),
  getFacilitiesCategories: (state) => {
    const categories = entityListSelector('categories')(state);
    return categories ? categories.filter(({ hasFacilities }) => hasFacilities) : [];
  },
  getSpaPackagesCategories: (state) => {
    const categories = entityListSelector('categories')(state);
    return categories ? categories.filter(({ hasSpaPackages }) => hasSpaPackages) : [];
  },
  getFacility: getEntityItemSelector('facilities'),
  getSpaPackages: (state, categoryId) =>
    getSpaPackagesEntityItemsList('spaPackages', categoryId)(state),
  getSpaPackagesPaginated: (state) => getPaginatedEntityList('spaPackages')(state).result,
  getSpaPackagesAll: (state, categoryId) =>
    getSpaEntityItemsList('spaPackagesAll', categoryId)(state),
  getSpaPackagesSortedByDisplayOrder: getItemsListSortedByDisplayOrder('spaPackagesAll'),
  getSpaDaysSortedByPriority: getSpaItemsListSortedByPriority('spaDays'),
  getSpaPackage: getEntityItemSelector('spaPackages'),
  getPostages: entityListSelector('postages'),
  getConfigurationValidatorList: getConfigurationValidatorList(),
};
